/** Price check, the cashier's shift, and held sales. */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { applyPromotions, compareSizes, promoSummary } from '@pos/shared';
import type { CatalogItem, Currency } from '@pos/shared';
import { useI18n, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useAuth } from '../../core/state/auth.js';
import { useToast } from '../../core/state/toast.js';
import { api } from '../../core/api.js';
import { shopKey } from '../../core/shop.js';
import { db, findByBarcode } from '../../core/db.js';
import { listenForScanner } from '../../core/scanner.js';
import { beepOk, beepUnknown } from '../../core/sound.js';
import { printDocument, escapeHtml } from '../../core/print.js';
import { Button, Confirm, Empty, Field, Loading, Modal, MoneyInput, Pill, Swatch } from '../../core/ui/ui.js';
import { CameraButton, Photo } from '../../core/ui/bits.js';
import { useRunningPromotions } from '../../core/promotions.js';

// ---------------------------------------------------------------------------
// Price check
// ---------------------------------------------------------------------------

export function PriceCheck() {
  const t = useT();
  const { lang } = useI18n();
  const { money } = useSettings();
  const [item, setItem] = useState<CatalogItem | null>(null);
  const [siblings, setSiblings] = useState<CatalogItem[]>([]);
  const [missing, setMissing] = useState<string | null>(null);
  const promos = useRunningPromotions();
  /** What one piece costs today, and under which offer. */
  const offer = (() => {
    if (!item) return null;
    const hit = applyPromotions(
      [{ id: 'x', productId: item.productId, categoryId: item.categoryId ?? null, brandId: item.brandId ?? null, qty: 1, unitPrice: item.price, openPrice: item.openPrice }],
      promos.filter((p) => p.kind === 'percent'),
    ).get('x');
    const onOffer = promos.find((p) => p.id === hit?.promoId) ??
      promos.find((p) => p.kind === 'bxgy' && applyPromotions(
        [{ id: 'x', productId: item.productId, categoryId: item.categoryId ?? null, brandId: item.brandId ?? null, qty: p.buyQty + p.getQty, unitPrice: item.price }],
        [p],
      ).size > 0);
    return onOffer ? { promo: onOffer, price: hit ? item.price - hit.amount : null } : null;
  })();

  const look = useCallback(async (code: string) => {
    const found = await findByBarcode(code);
    if (!found) {
      try {
        const fromServer = await api<{ item: CatalogItem; siblings: CatalogItem[] }>(
          `/catalog/price-check/${encodeURIComponent(code)}`,
        );
        beepOk();
        setItem(fromServer.item);
        setSiblings(fromServer.siblings);
        setMissing(null);
      } catch {
        beepUnknown();
        setMissing(code);
        setItem(null);
      }
      return;
    }
    beepOk();
    setMissing(null);
    setItem(found);
    const rows = await db.items.where('productId').equals(found.productId).toArray();
    setSiblings(rows.sort((a, b) => compareSizes(a.size, b.size) || a.colorEn.localeCompare(b.colorEn)));
  }, []);

  useEffect(() => listenForScanner({ onScan: (c) => void look(c) }), [look]);

  const nm = (i: CatalogItem) => (lang === 'ar' ? i.nameAr || i.nameEn : i.nameEn || i.nameAr);
  const cl = (i: CatalogItem) => (lang === 'ar' ? i.colorAr || i.colorEn : i.colorEn || i.colorAr);

  return (
    <div style={{ maxWidth: 760, marginInline: 'auto' }} className="col">
      <div className="row">
        <input
          data-scan-target="true"
          autoFocus
          placeholder={t('priceCheck.scan')} aria-label={t('priceCheck.scan')}
          className="big"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              void look((e.target as HTMLInputElement).value);
              (e.target as HTMLInputElement).value = '';
            }
          }}
        />
        <CameraButton onScan={(c) => void look(c)} />
      </div>

      {missing ? (
        <div className="note-danger">{t('checkout.unknownBarcode', { code: missing })}</div>
      ) : null}

      {!item ? (
        <Empty icon="🔍" title={t('priceCheck.title')} hint={t('priceCheck.hint')} />
      ) : (
        <>
          <div className="card center">
            <Photo url={item.imageUrl} className="picker-photo" />
            <h2>{nm(item)}</h2>
            <div className="muted small">{lang === 'ar' ? item.nameEn : item.nameAr}</div>
            {offer?.price != null ? (
              <div style={{ marginBlock: 'var(--s-4)' }}>
                <div className="tnum muted" style={{ textDecoration: 'line-through' }}>{money(item.price)}</div>
                <div style={{ fontSize: 'var(--t-3xl)', fontWeight: 700, color: 'var(--ok)' }} className="tnum">{money(offer.price)}</div>
              </div>
            ) : (
              <div style={{ fontSize: 'var(--t-3xl)', fontWeight: 700, marginBlock: 'var(--s-4)' }} className="tnum">
                {money(item.price)}
              </div>
            )}
            {offer ? (
              <div className="promo-preview" style={{ marginBlockEnd: 'var(--s-3)', justifyContent: 'center' }}>
                <span aria-hidden>🏷</span> {lang === 'ar' ? offer.promo.nameAr || offer.promo.nameEn : offer.promo.nameEn || offer.promo.nameAr}
                {' · '}{promoSummary(offer.promo, lang === 'ar' ? 'ar' : 'en')}
              </div>
            ) : null}
            <div className="row" style={{ justifyContent: 'center' }}>
              <Pill>{item.size}</Pill>
              <Pill>
                <Swatch hex={item.colorHex} /> {cl(item)}
              </Pill>
              <Pill kind={item.stock > 0 ? 'ok' : 'danger'}>
                {item.stock > 0 ? `${item.stock} ${t('priceCheck.inStock')}` : t('priceCheck.soldOut')}
              </Pill>
              {item.finalSale ? <Pill kind="warn">{t('checkout.finalSale')}</Pill> : null}
            </div>
          </div>

          <div className="card">
            <h3>{t('priceCheck.otherSizes')}</h3>
            <div className="table-wrap" style={{ marginBlockStart: 'var(--s-3)' }}>
              <table className="cards">
                <thead>
                  <tr>
                    <th>{t('common.size')}</th>
                    <th>{t('common.colour')}</th>
                    <th className="num">{t('common.price')}</th>
                    <th className="num">{t('common.stock')}</th>
                  </tr>
                </thead>
                <tbody>
                  {siblings.map((s) => (
                    <tr key={s.variantId} style={s.variantId === item.variantId ? { background: 'var(--accent-soft)' } : undefined}>
                      <td className="strong" data-label={t('common.size')}>{s.size}</td>
                      <td data-label={t('common.colour')}>
                        <Swatch hex={s.colorHex} /> {cl(s)}
                      </td>
                      <td className="num tnum" data-label={t('common.price')}>{money(s.price)}</td>
                      <td className="num" data-label={t('common.stock')}>
                        <Pill kind={s.stock > 0 ? 'ok' : 'danger'}>{s.stock}</Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The cashier's shift
// ---------------------------------------------------------------------------

interface ShiftReport {
  shiftId: string;
  userName: string;
  deviceName: string;
  openedAt: string;
  closedAt: string | null;
  status: string;
  currencies: {
    code: string;
    opening: number;
    takings: number;
    changeGiven: number;
    expected: number;
    counted: number | null;
    difference: number | null;
  }[];
  cashIls: number;
  cardIls: number;
  otherIls: number;
  netSales: number;
  transactions: number;
}

export function Shift() {
  const t = useT();
  const { settings, currencies, money, amount, dateTime } = useSettings();
  const { user } = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState<{ id: string } | null>(null);
  const [report, setReport] = useState<ShiftReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState<Record<string, number>>({});
  const [closing, setClosing] = useState<Record<string, number>>({});
  const [closingNow, setClosingNow] = useState(false);
  const [drawerReason, setDrawerReason] = useState<string | null>(null);

  const active = currencies.filter((c) => c.active);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api<{ id: string } | null>('/sales/shifts/open');
      setOpen(s);
      if (s) {
        localStorage.setItem(shopKey('pos.shift'), JSON.stringify(s));
        setReport(await api<ShiftReport>(`/sales/shifts/${s.id}/report`));
      } else {
        localStorage.removeItem(shopKey('pos.shift'));
        setReport(null);
      }
    } catch {
      toast.warn(t('net.needOnline'));
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const openShift = async () => {
    try {
      await api('/sales/shifts/open', { body: { openingCash: opening } });
      toast.ok(t('shift.opened'));
      await load();
    } catch (e) {
      toast.err((e as Error).message);
    }
  };

  const closeShift = async () => {
    if (!open) return;
    try {
      const r = await api<ShiftReport>(`/sales/shifts/${open.id}/close`, { body: { closingCash: closing } });
      toast.ok(t('shift.closed'));
      setClosingNow(false);
      printZ(r);
      await load();
    } catch (e) {
      toast.err((e as Error).message);
    }
  };

  const printZ = (r: ShiftReport) => {
    const rows = r.currencies
      .map(
        (c) => `<tr>
          <td>${c.code}</td>
          <td class="num">${amount(c.opening, c.code)}</td>
          <td class="num">${amount(c.takings, c.code)}</td>
          <td class="num">${amount(c.changeGiven, c.code)}</td>
          <td class="num">${amount(c.expected, c.code)}</td>
          <td class="num">${c.counted === null ? '—' : amount(c.counted, c.code)}</td>
          <td class="num"><strong>${c.difference === null ? '—' : amount(c.difference, c.code)}</strong></td>
        </tr>`,
      )
      .join('');

    void printDocument(
      `Z report ${r.shiftId.slice(0, 8)}`,
      `<h1>${escapeHtml(t('shift.zReport'))}</h1>
       <div class="sub">${escapeHtml(settings.storeNameEn)} · ${escapeHtml(settings.storeNameAr)}<br>
         ${escapeHtml(r.userName)} · ${escapeHtml(r.deviceName)}<br>
         ${escapeHtml(dateTime(r.openedAt))} → ${escapeHtml(r.closedAt ? dateTime(r.closedAt) : '—')}</div>
       <div class="cards">
         <div class="kpi"><div class="k">${escapeHtml(t('reports.netSales'))}</div><div class="v">${escapeHtml(money(r.netSales))}</div></div>
         <div class="kpi"><div class="k">${escapeHtml(t('reports.transactions'))}</div><div class="v">${r.transactions}</div></div>
         <div class="kpi"><div class="k">${escapeHtml(t('pay.cash'))}</div><div class="v">${escapeHtml(money(r.cashIls))}</div></div>
         <div class="kpi"><div class="k">${escapeHtml(t('pay.card'))}</div><div class="v">${escapeHtml(money(r.cardIls))}</div></div>
       </div>
       <table>
         <thead><tr>
           <th>${escapeHtml(t('common.currency'))}</th>
           <th class="num">${escapeHtml(t('shift.openingCash'))}</th>
           <th class="num">${escapeHtml(t('shift.takings'))}</th>
           <th class="num">${escapeHtml(t('pay.changeDue'))}</th>
           <th class="num">${escapeHtml(t('shift.expected'))}</th>
           <th class="num">${escapeHtml(t('shift.counted'))}</th>
           <th class="num">${escapeHtml(t('shift.difference'))}</th>
         </tr></thead>
         <tbody>${rows}</tbody>
       </table>
       <div class="foot">${escapeHtml(new Date().toLocaleString('en-GB'))}</div>`,
    );
  };

  if (loading) return <Loading />;

  if (!open) {
    return (
      <div className="card" style={{ maxWidth: 520, marginInline: 'auto' }}>
        <h2>{t('shift.open')}</h2>
        <p className="small muted">{t('shift.countEach')}</p>
        <div className="col" style={{ marginBlockStart: 'var(--s-4)' }}>
          {active.map((c: Currency) => (
            <Field key={c.code} label={`${c.symbol} ${c.code}`}>
              <MoneyInput
                value={opening[c.code] ?? 0}
                onValue={(v) => setOpening((o) => ({ ...o, [c.code]: v }))}
                decimals={c.decimals}
              />
            </Field>
          ))}
          <Button variant="primary" size="lg" block onClick={() => void openShift()}>
            {t('shift.open')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="col" style={{ maxWidth: 760, marginInline: 'auto' }}>
      <div className="card">
        <div className="card-head">
          <h2>{t('shift.title')}</h2>
          <div className="spacer" />
          <Pill kind="ok">{t('shift.startedAt')} {report ? dateTime(report.openedAt) : ''}</Pill>
        </div>

        <div className="grid c3">
          <div className="kpi">
            <div className="k">{t('reports.netSales')}</div>
            <div className="v">{money(report?.netSales ?? 0)}</div>
          </div>
          <div className="kpi">
            <div className="k">{t('shift.salesCount')}</div>
            <div className="v">{report?.transactions ?? 0}</div>
          </div>
          <div className="kpi">
            <div className="k">{t('pay.card')}</div>
            <div className="v">{money(report?.cardIls ?? 0)}</div>
          </div>
        </div>

        <div className="table-wrap" style={{ marginBlockStart: 'var(--s-5)' }}>
          <table className="cards">
            <thead>
              <tr>
                <th>{t('common.currency')}</th>
                <th className="num">{t('shift.openingCash')}</th>
                <th className="num">{t('shift.takings')}</th>
                <th className="num">{t('shift.expected')}</th>
              </tr>
            </thead>
            <tbody>
              {report?.currencies.map((c) => (
                <tr key={c.code}>
                  <td className="strong" data-label={t('common.currency')}>{c.code}</td>
                  <td className="num tnum" data-label={t('shift.openingCash')}>{amount(c.opening, c.code)}</td>
                  <td className="num tnum" data-label={t('shift.takings')}>{amount(c.takings, c.code)}</td>
                  <td className="num tnum strong" data-label={t('shift.expected')}>{amount(c.expected, c.code)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="btn-row" style={{ marginBlockStart: 'var(--s-5)' }}>
          <Button onClick={() => setDrawerReason('')}>{t('shift.drawerOpen')}</Button>
          <div className="spacer" />
          <Button variant="primary" onClick={() => setClosingNow(true)}>
            {t('shift.close')}
          </Button>
        </div>
      </div>

      {closingNow && report ? (
        <Modal
          title={t('shift.close')}
          onClose={() => setClosingNow(false)}
          footer={
            <>
              <Button onClick={() => setClosingNow(false)}>{t('common.cancel')}</Button>
              <div className="spacer" />
              <Button variant="primary" onClick={() => void closeShift()}>
                {t('shift.close')}
              </Button>
            </>
          }
        >
          <p className="small muted">{t('shift.countEach')}</p>
          <div className="col" style={{ marginBlockStart: 'var(--s-4)' }}>
            {report.currencies.map((c) => {
              const cur = currencies.find((x) => x.code === c.code);
              const counted = closing[c.code] ?? 0;
              const diff = counted - c.expected;
              return (
                <div key={c.code} className="row" style={{ alignItems: 'flex-end' }}>
                  <Field label={`${cur?.symbol ?? ''} ${c.code}`}>
                    <MoneyInput
                      value={counted}
                      onValue={(v) => setClosing((o) => ({ ...o, [c.code]: v }))}
                      decimals={cur?.decimals ?? 2}
                    />
                  </Field>
                  <div style={{ minWidth: 140 }}>
                    <div className="xsmall muted">{t('shift.expected')} {amount(c.expected, c.code)}</div>
                    <div className={`small strong ${diff === 0 ? '' : diff > 0 ? 'up' : 'down'}`}
                         style={{ color: diff === 0 ? 'var(--ok)' : diff > 0 ? 'var(--warn)' : 'var(--danger)' }}>
                      {diff === 0
                        ? t('shift.exact')
                        : `${amount(Math.abs(diff), c.code)} ${diff > 0 ? t('shift.over') : t('shift.short')}`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Modal>
      ) : null}

      {drawerReason !== null ? (
        <Modal
          title={t('shift.drawerOpen')}
          onClose={() => setDrawerReason(null)}
          footer={
            <>
              <Button onClick={() => setDrawerReason(null)}>{t('common.cancel')}</Button>
              <div className="spacer" />
              <Button
                variant="primary"
                onClick={() => {
                  void api('/sales/drawer/open', { body: { reason: drawerReason } });
                  toast.ok(t('common.done'));
                  setDrawerReason(null);
                }}
              >
                {t('shift.drawerOpen')}
              </Button>
            </>
          }
        >
          <Field label={t('shift.drawerReason')}>
            <input value={drawerReason} onChange={(e) => setDrawerReason(e.target.value)} autoFocus />
          </Field>
        </Modal>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Held sales
// ---------------------------------------------------------------------------

export function Held() {
  const t = useT();
  const { dateTime } = useSettings();
  const toast = useToast();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [dropping, setDropping] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api<Record<string, unknown>[]>('/sales/held/list'));
    } catch {
      toast.warn(t('net.needOnline'));
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <Loading />;
  if (rows.length === 0) return <Empty icon="⏸️" title={t('checkout.empty')} hint={t('checkout.hold')} />;

  return (
    <div className="card flush">
      <div className="table-wrap">
        <table className="cards">
          <thead>
            <tr>
              <th>{t('common.date')}</th>
              <th>{t('common.cashier')}</th>
              <th>{t('common.items')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r.id)}>
                <td data-label={t('common.date')}>{dateTime(String(r.created_at))}</td>
                <td data-label={t('common.cashier')}>{String(r.user_name ?? '')}</td>
                <td className="strong" data-label={t('common.items')}>{String(r.label ?? '')}</td>
                <td className="num">
                  <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                    <Button size="sm" variant="ghost" onClick={() => setDropping(String(r.id))}>
                      {t('common.delete')}
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => navigate('/checkout', { state: { resumeHeld: r } })}
                    >
                      {t('checkout.resume')}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dropping ? (
        <Confirm
          title={t('common.delete')}
          message={t('checkout.cancelConfirm')}
          confirmLabel={t('common.delete')}
          danger
          onConfirm={() => {
            void api(`/sales/held/${dropping}`, { method: 'DELETE' }).then(load);
            setDropping(null);
          }}
          onCancel={() => setDropping(null)}
        />
      ) : null}
    </div>
  );
}
